import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db, auth } from './firebase';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
} from 'firebase/auth';

const inp = {
  width: '100%', padding: '11px 14px', borderRadius: 10,
  border: '1px solid #E5E7EB', fontSize: 14,
  backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box',
};

const NETWORKS = [
  { id: 'mtn',        label: 'MTN MoMo',         color: '#F59E0B', icon: '📱' },
  { id: 'telecel',    label: 'Telecel Cash',      color: '#EF4444', icon: '📱' },
  { id: 'airteltigo', label: 'AirtelTigo Money',  color: '#3B82F6', icon: '📱' },
  { id: 'bank',       label: 'Bank Transfer',     color: '#1D9E75', icon: '🏦' },
];

export default function BankAccountTab({ pharmacyId, pharmacyData }) {
  const [saved, setSaved]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [editing, setEditing]         = useState(false);
  const [saving, setSaving]           = useState(false);

  // Form state
  const [network, setNetwork]         = useState('mtn');
  const [momoNumber, setMomoNumber]   = useState('');
  const [momoName, setMomoName]       = useState('');
  const [bankName, setBankName]       = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [branch, setBranch]           = useState('');

  // Gmail verification
  const [verifyStep, setVerifyStep]   = useState(false);
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying]     = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const user = auth.currentUser;

  useEffect(() => {
    if (!pharmacyId) return;
    onValue(ref(db, `pharmacies/${pharmacyId}/paymentAccount`), snap => {
      if (snap.exists()) {
        const data = snap.val();
        setSaved(data);
        setNetwork(data.network        || 'mtn');
        setMomoNumber(data.momoNumber  || '');
        setMomoName(data.momoName      || '');
        setBankName(data.bankName      || '');
        setAccountNumber(data.accountNumber || '');
        setAccountName(data.accountName    || '');
        setBranch(data.branch          || '');
      }
      setLoading(false);
    });
  }, [pharmacyId]);

  // Step 1 — verify Gmail password before showing edit form
  const handleEditClick = async () => {
    // emailVerified on the cached user object doesn't update itself after
    // clicking the link in the email — reload picks up the real status
    // without forcing a re-login.
    try { await user?.reload(); } catch (e) {}
    if (!user?.emailVerified) {
      try {
        await sendEmailVerification(user);
        alert(`⚠️ Please verify your email before changing payout details. A verification link was just sent to ${user.email} — check your inbox and spam folder.`);
      } catch (e) {
        alert(e.code === 'auth/too-many-requests'
          ? '⚠️ A verification email was already sent recently. Please check your inbox and spam folder (it can take a few minutes) before requesting another.'
          : `⚠️ Could not send a verification email: ${e.message}. Please try again shortly.`);
      }
      return;
    }
    setVerifyStep(true);
    setPassword('');
    setVerifyError('');
  };

  const handleVerify = async () => {
    if (!password.trim()) { setVerifyError('Please enter your password'); return; }
    setVerifying(true); setVerifyError('');
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      setVerifyStep(false);
      setEditing(true);
    } catch (e) {
      setVerifyError('Incorrect password. Please try again.');
    } finally { setVerifying(false); }
  };

  const handleSave = async () => {
    if (network === 'bank') {
      if (!bankName.trim())      { alert('Bank name is required'); return; }
      if (!accountNumber.trim()) { alert('Account number is required'); return; }
      if (!accountName.trim())   { alert('Account name is required'); return; }
    } else {
      if (!momoNumber.trim()) { alert('MoMo number is required'); return; }
      if (!momoName.trim())   { alert('Account name is required'); return; }
    }

    setSaving(true);
    try {
      const data = {
        network,
        momoNumber:     momoNumber.trim(),
        momoName:       momoName.trim(),
        bankName:       bankName.trim(),
        accountNumber:  accountNumber.trim(),
        accountName:    accountName.trim(),
        branch:         branch.trim(),
        updatedAt:      new Date().toISOString(),
        updatedBy:      user?.email || '',
        pharmacyId,
        pharmacyName:   pharmacyData?.pharmacyName || '',
      };
      await update(ref(db, `pharmacies/${pharmacyId}/paymentAccount`), data);
      setSaved(data);
      setEditing(false);
      alert('✅ Payment account saved successfully!');
    } catch { alert('Error saving. Please try again.'); }
    finally { setSaving(false); }
  };

  const selectedNetwork = NETWORKS.find(n => n.id === network);

  if (loading) return <div style={{ textAlign:'center', padding:60, color:'#888' }}>Loading payment account...</div>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* HEADER */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontWeight:700, margin:'0 0 4px', fontSize:20 }}>🏦 Payment Account</h2>
        <p style={{ color:'#888', fontSize:13, margin:0 }}>
          This is where PharmDev sends your daily payouts after customers pay for their medicines.
        </p>
      </div>

      {/* INFO BANNER */}
      <div style={{ backgroundColor:'#E8F8F2', borderRadius:12, padding:16, marginBottom:24, border:'1px solid #B2DFD0', display:'flex', gap:12 }}>
        <span style={{ fontSize:28 }}>💰</span>
        <div>
          <p style={{ fontWeight:700, fontSize:14, color:'#1D6B52', margin:'0 0 4px' }}>How payouts work</p>
          <p style={{ fontSize:13, color:'#1D6B52', margin:0, lineHeight:1.6 }}>
            After every delivered order, PharmDev sends <strong>100% of your pharmacy price</strong> to this account every day by 8 PM.
            PharmDev's 30% commission is charged separately on top — you never lose from your listed price.
            <br /><strong>🔐 Changing this account requires your login password for security.</strong>
          </p>
        </div>
      </div>

      {/* PASSWORD VERIFICATION MODAL */}
      {verifyStep && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ backgroundColor:'#FFF', borderRadius:16, padding:32, maxWidth:420, width:'100%' }}>
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <p style={{ fontSize:40, margin:'0 0 10px' }}>🔐</p>
              <h3 style={{ fontWeight:700, margin:'0 0 6px', fontSize:18 }}>Verify Your Identity</h3>
              <p style={{ color:'#888', fontSize:13, margin:0, lineHeight:1.6 }}>
                Enter your PharmDev login password to make changes to your payment account.
              </p>
            </div>

            <div style={{ backgroundColor:'#FEF3C7', borderRadius:10, padding:12, marginBottom:20, border:'1px solid #FCD34D' }}>
              <p style={{ fontSize:13, color:'#92400E', margin:0, fontWeight:600 }}>
                ⚠️ This is a security check. Only the account owner can change payout details.
              </p>
            </div>

            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>
              Your email: <span style={{ color:'#1D9E75', fontWeight:700 }}>{user?.email}</span>
            </label>

            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6, marginTop:14 }}>
              Password *
            </label>
            <div style={{ position:'relative', marginBottom:16 }}>
              <input
                style={{ ...inp, paddingRight:50 }}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your login password"
                value={password}
                onChange={e => { setPassword(e.target.value); setVerifyError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleVerify()}
                autoFocus
              />
              <button
                style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:18 }}
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            {verifyError && (
              <div style={{ backgroundColor:'#FEE2E2', borderRadius:8, padding:10, marginBottom:14 }}>
                <p style={{ color:'#B91C1C', fontSize:13, margin:0 }}>❌ {verifyError}</p>
              </div>
            )}

            <div style={{ display:'flex', gap:10 }}>
              <button
                style={{ flex:1, padding:'12px', border:'1px solid #E5E7EB', borderRadius:10, cursor:'pointer', backgroundColor:'#F7F8FA', color:'#555', fontSize:14 }}
                onClick={() => setVerifyStep(false)}
              >
                Cancel
              </button>
              <button
                style={{ flex:2, padding:'12px', backgroundColor:verifying?'#A8D5C2':'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:verifying?'not-allowed':'pointer', fontWeight:700, fontSize:14 }}
                onClick={handleVerify}
                disabled={verifying}
              >
                {verifying ? '🔐 Verifying...' : '✓ Confirm & Edit Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CURRENT SAVED ACCOUNT */}
      {saved && !editing && (
        <div style={{ backgroundColor:'#FFF', borderRadius:14, border:'1px solid #E5E7EB', overflow:'hidden', marginBottom:20 }}>
          <div style={{ backgroundColor: selectedNetwork?.color || '#1D9E75', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <p style={{ color:'#FFF', fontWeight:700, fontSize:16, margin:'0 0 2px' }}>
                {selectedNetwork?.icon} {selectedNetwork?.label}
              </p>
              <p style={{ color:'rgba(255,255,255,0.8)', fontSize:12, margin:0 }}>Active payout account</p>
            </div>
            <span style={{ backgroundColor:'rgba(255,255,255,0.2)', color:'#FFF', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
              ✅ Verified
            </span>
          </div>
          <div style={{ padding:20 }}>
            {saved.network === 'bank' ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {[
                  ['Bank Name',      saved.bankName],
                  ['Account Number', saved.accountNumber],
                  ['Account Name',   saved.accountName],
                  ['Branch',         saved.branch || '—'],
                ].map(([l,v]) => (
                  <div key={l} style={{ backgroundColor:'#F7F8FA', borderRadius:8, padding:12 }}>
                    <p style={{ fontSize:11, color:'#888', margin:'0 0 4px' }}>{l}</p>
                    <p style={{ fontSize:14, fontWeight:700, color:'#1A1A1A', margin:0 }}>{v}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {[
                  ['Network',      selectedNetwork?.label],
                  ['MoMo Number',  saved.momoNumber],
                  ['Account Name', saved.momoName],
                  ['Pharmacy',     saved.pharmacyName || pharmacyData?.pharmacyName],
                ].map(([l,v]) => (
                  <div key={l} style={{ backgroundColor:'#F7F8FA', borderRadius:8, padding:12 }}>
                    <p style={{ fontSize:11, color:'#888', margin:'0 0 4px' }}>{l}</p>
                    <p style={{ fontSize:14, fontWeight:700, color:'#1A1A1A', margin:0 }}>{v}</p>
                  </div>
                ))}
              </div>
            )}

            {saved.updatedAt && (
              <p style={{ fontSize:11, color:'#888', margin:'14px 0 0' }}>
                Last updated: {new Date(saved.updatedAt).toLocaleString()} by {saved.updatedBy}
              </p>
            )}

            <button
              style={{ marginTop:16, width:'100%', padding:'12px', backgroundColor:'#FEF3C7', color:'#92400E', border:'1px solid #FCD34D', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}
              onClick={handleEditClick}
            >
              🔐 Change Payment Account (requires password)
            </button>
          </div>
        </div>
      )}

      {/* NO ACCOUNT YET */}
      {!saved && !editing && (
        <div style={{ backgroundColor:'#FFF', borderRadius:14, border:'2px dashed #E5E7EB', padding:40, textAlign:'center', marginBottom:20 }}>
          <p style={{ fontSize:48, margin:'0 0 12px' }}>🏦</p>
          <p style={{ fontWeight:700, fontSize:16, color:'#1A1A1A', margin:'0 0 6px' }}>No payment account set up yet</p>
          <p style={{ fontSize:13, color:'#888', margin:'0 0 20px' }}>
            Set up your MoMo or bank account to receive daily payouts from PharmDev
          </p>
          <button
            style={{ padding:'12px 28px', backgroundColor:'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}
            onClick={handleEditClick}
          >
            🏦 Set Up Payment Account
          </button>
        </div>
      )}

      {/* EDIT FORM */}
      {editing && (
        <div style={{ backgroundColor:'#FFF', borderRadius:14, border:'1px solid #E5E7EB', padding:24, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <h3 style={{ fontWeight:700, margin:0, fontSize:16 }}>
              {saved ? '✏️ Update Payment Account' : '➕ Set Up Payment Account'}
            </h3>
            <button style={{ background:'none', border:'none', cursor:'pointer', color:'#888', fontSize:20 }} onClick={()=>setEditing(false)}>✕</button>
          </div>

          {/* NETWORK SELECTOR */}
          <div style={{ marginBottom:20 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:10 }}>
              Payout Method *
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
              {NETWORKS.map(n => (
                <button
                  key={n.id}
                  style={{ padding:'12px 16px', borderRadius:10, border:`2px solid ${network===n.id?n.color:'#E5E7EB'}`, backgroundColor:network===n.id?n.color+'15':'#FFF', cursor:'pointer', display:'flex', alignItems:'center', gap:10, fontWeight:network===n.id?700:400, color:network===n.id?n.color:'#555', fontSize:14 }}
                  onClick={() => setNetwork(n.id)}
                >
                  <span style={{ fontSize:20 }}>{n.icon}</span>
                  {n.label}
                  {network === n.id && <span style={{ marginLeft:'auto', fontSize:16 }}>✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* MOMO FIELDS */}
          {network !== 'bank' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>
                  {selectedNetwork?.label} Number *
                </label>
                <input
                  style={inp}
                  placeholder="e.g. 0244000000"
                  value={momoNumber}
                  onChange={e => setMomoNumber(e.target.value)}
                  maxLength={15}
                  type="tel"
                />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>
                  Account Name *
                </label>
                <input
                  style={inp}
                  placeholder="Name on MoMo account"
                  value={momoName}
                  onChange={e => setMomoName(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* BANK FIELDS */}
          {network === 'bank' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Bank Name *</label>
                <input style={inp} placeholder="e.g. GCB Bank, Ecobank" value={bankName} onChange={e=>setBankName(e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Account Number *</label>
                <input style={inp} placeholder="Your account number" value={accountNumber} onChange={e=>setAccountNumber(e.target.value)} type="tel" />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Account Name *</label>
                <input style={inp} placeholder="Name on bank account" value={accountName} onChange={e=>setAccountName(e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Branch <span style={{ fontWeight:400, color:'#888' }}>(optional)</span></label>
                <input style={inp} placeholder="e.g. Accra Main Branch" value={branch} onChange={e=>setBranch(e.target.value)} />
              </div>
            </div>
          )}

          {/* SECURITY NOTE */}
          <div style={{ backgroundColor:'#F0FDF4', borderRadius:10, padding:12, marginBottom:20, border:'1px solid #BBF7D0' }}>
            <p style={{ fontSize:12, color:'#15803D', margin:0, lineHeight:1.6 }}>
              🔒 <strong>Your account is secure.</strong> Only you can change these details — every update is logged with your email and timestamp. PharmDev staff cannot change your payout account without your password.
            </p>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button style={{ flex:1, padding:'12px', border:'1px solid #E5E7EB', borderRadius:10, cursor:'pointer', backgroundColor:'#F7F8FA', color:'#555', fontSize:14 }} onClick={()=>setEditing(false)}>
              Cancel
            </button>
            <button
              style={{ flex:2, padding:'12px', backgroundColor:saving?'#A8D5C2':'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:saving?'not-allowed':'pointer', fontWeight:700, fontSize:14 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : '✅ Save Payment Account'}
            </button>
          </div>
        </div>
      )}

      {/* PAYOUT HISTORY */}
      <div style={{ backgroundColor:'#FFF', borderRadius:14, border:'1px solid #E5E7EB', padding:20 }}>
        <h3 style={{ fontWeight:700, fontSize:15, margin:'0 0 14px' }}>📋 Payout Schedule</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {[
            { icon:'📅', label:'Payout Frequency', value:'Daily — every day' },
            { icon:'🕗', label:'Payout Time',       value:'By 8:00 PM Ghana time' },
            { icon:'💸', label:'Your Share',         value:'100% of your listed prices' },
            { icon:'📱', label:'Method',             value:saved ? `${selectedNetwork?.label}` : 'Not set up yet' },
          ].map((item,i) => (
            <div key={i} style={{ backgroundColor:'#F7F8FA', borderRadius:10, padding:14 }}>
              <p style={{ fontSize:20, margin:'0 0 6px' }}>{item.icon}</p>
              <p style={{ fontSize:11, color:'#888', margin:'0 0 3px' }}>{item.label}</p>
              <p style={{ fontSize:13, fontWeight:700, color:'#1A1A1A', margin:0 }}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}